import { Typography } from "@/components/app";
import { SectionTitle } from "./SectionTitle";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Plus, Minus } from "lucide-react";
import { useState } from "react";
import { motion } from "framer-motion";
import { fadeUp, staggerContainer, viewportOnce } from "@/lib/animations";

interface FAQ {
  question: string;
  answer: string;
}

const faqs: FAQ[] = [
  {
    question: "Is The Jewellery BIS Hallmarked?",
    answer:
      "Yes, all our gold jewellery is BIS Hallmark certified as mandated by the Government of India. Each gold piece carries a unique HUID (Hallmark Unique Identification) number that you can verify on the BIS Care portal to confirm purity and authenticity.",
  },
  {
    question: "How Are Gold & Silver Prices Calculated?",
    answer:
      "Our jewellery prices are based on live Indian spot rates for gold and silver, plus making charges and 3% GST. Since metal prices fluctuate daily, the final price is locked at the prevailing rate at the time your payment is confirmed.",
  },
  {
    question: "What Gold & Silver Purity Options Are Available?",
    answer:
      "We offer gold jewellery in 24K (99.9%), 22K (91.6%), 18K (75%), and 14K (58.5%) purity. For silver, we offer 999 pure silver and 925 sterling silver options. The purity is clearly mentioned on each product page and verified through BIS certification.",
  },
  {
    question: "What Payment Methods Are Supported?",
    answer:
      "We support UPI (GPay, PhonePe, Paytm), credit/debit cards (Visa, Mastercard, RuPay), net banking, and EMI options on select bank cards for higher-value orders. All transactions are secured with 256-bit SSL encryption.",
  },
  {
    question: "Is KYC Required To Buy Jewellery?",
    answer:
      "For purchases above ₹2,00,000, a valid PAN card is required as per Income Tax regulations. For high-value orders, additional KYC documents may be requested in compliance with PMLA guidelines. This is a regulatory requirement to ensure secure and transparent transactions.",
  },
  {
    question: "How Is My Jewellery Delivered?",
    answer:
      "All orders are shipped in tamper-proof, secure packaging with full insurance coverage for the declared jewellery value. Delivery typically takes 5–10 business days within India. You can track your order in real-time through the app using your tracking number.",
  },
  {
    question: "Is My Payment Secure?",
    answer:
      "Absolutely! We use RazorPay payment gateways with 256-bit SSL encryption for all transactions. We never store your card details on our servers. High-value jewellery transactions are further protected by multi-factor authentication.",
  },
];

export function FAQSection() {
  const [openItem, setOpenItem] = useState<string>("item-0");

  return (
    <section id="faq" className="py-6 md:py-10 lg:py-12">
      <div className="wrapper">
        {/* Section Title */}
        <motion.div
          className="mb-6 md:mb-10"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          <SectionTitle
            badge="FAQ"
            title="Frequently Asked Questions"
            subtitle="Everything you need to know about shopping on our app."
            centered
          />
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          className="max-w-[838px] mx-auto"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          <Accordion
            type="single"
            collapsible
            value={openItem}
            onValueChange={setOpenItem}
            className="space-y-4"
          >
            {faqs.map((faq, index) => {
              const itemValue = `item-${index}`;
              const isOpen = openItem === itemValue;

              return (
                <motion.div key={index} variants={fadeUp}>
                  <AccordionItem
                    value={itemValue}
                    className="border border-[#CBC1BC] rounded-[12px] p-0 bg-background transition-all duration-300"
                  >
                    <AccordionTrigger className="md:p-5 p-4 hover:no-underline [&>svg]:hidden">
                      <Typography
                        variant="h6"
                        className="font-semibold text-left"
                      >
                        {faq.question}
                      </Typography>
                      <div className="ml-auto px-4 transition-transform duration-300">
                        {isOpen ? (
                          <Minus className="h-5 w-5 text-primary shrink-0" />
                        ) : (
                          <Plus className="h-5 w-5 text-primary shrink-0" />
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="md:px-5 px-4 pb-4 pt-0 transition-all duration-300">
                      <Typography variant="body2" color="muted">
                        {faq.answer}
                      </Typography>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              );
            })}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
